/**
 * SupportActionsPanel — wat support kan repareren, en wat bewust niet.
 *
 * HET GAT DAT DIT DICHT
 *
 * Krijgt support een supportnummer van een school, dan was tot nu toe het enige
 * beschikbare middel "voortgang resetten". Dat is het grofste instrument dat er
 * is en het lost geen enkele echte melding op. De vier RPC's uit 0042 stonden
 * klaar zonder aanroeper — het vijfde geval in dit project van "de beveiliging
 * staat er, het bedieningspaneel niet".
 *
 * DRIE CATEGORIEËN, EXPLICIET GESCHEIDEN
 *
 * Dat onderscheid is het hele punt van dit paneel:
 *
 *   1. REDEN NODIG   → structurele reparaties. Geen venster, want er wordt niets
 *                      van het kind gelezen. Wel een reden en een logregel.
 *   2. VENSTER NODIG → alles waarbij gegevens van het kind worden gelezen of
 *                      geïnterpreteerd. Staat elders in dit portaal.
 *   3. KAN BEWUST NIET → met uitleg, niet als uitgeschakeld veld. Een grijs
 *                      invoervak is een belofte dat het er nog komt; deze drie
 *                      komen er niet.
 *
 * WAAROM DE REDEN EEN VRIJ VELD IS EN GEEN KEUZELIJST
 *
 * Een keuzelijst wordt de eerste optie. De reden moet de melding beschrijven die
 * support kreeg, en die is per geval anders — dat is precies wat hem bruikbaar
 * maakt als verantwoording. De server eist minstens tien tekens; dit scherm zegt
 * dat vóór het versturen in plaats van erna.
 */

import React, { useState } from 'react';
import { AlertTriangle, KeyRound, LinkIcon, ShieldOff, Unlink, Copy } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { issueLinkCode, revokeRecoveryCode, detachLearnerFromClasses, unlinkParent } from '../../../../data/supabase/supportActions';
type ActionId = 'issue_code' | 'revoke_recovery' | 'detach_classes' | 'unlink_parent';
interface SupportActionsPanelProps {
  subjectId: string;
  subjectName: string;
  role: 'learner' | 'teacher' | 'parent' | 'admin';
  /**
   * Ouders die dit apparaat kent, om te kunnen ontkoppelen.
   *
   * Kan leeg zijn: op het apparaat van een beheerder heeft zo'n ouder per
   * definitie nooit ingelogd. Dan zegt het paneel dat het de lijst niet kent, in
   * plaats van te suggereren dat er niemand gekoppeld is — dat laatste is een
   * bewering die we hier niet kunnen waarmaken.
   */
  knownParents: Array<{
    id: string;
    name: string;
  }>;
}
export function SupportActionsPanel({
  subjectId,
  subjectName,
  role,
  knownParents
}: SupportActionsPanelProps) {
  const [open, setOpen] = useState<ActionId | null>(null);
  const [reason, setReason] = useState('');
  const [parentId, setParentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issuedCode, setIssuedCode] = useState<string | null>(null);
  const reasonTooShort = reason.trim().length < 10;
  const close = () => {
    setOpen(null);
    setReason('');
    setParentId('');
    setError(null);
  };
  const run = async (action: ActionId) => {
    setBusy(true);
    setError(null);
    setResult(null);
    setIssuedCode(null);
    const trimmed = reason.trim();
    const res = action === 'issue_code' ? await issueLinkCode(subjectId, trimmed) : action === 'revoke_recovery' ? await revokeRecoveryCode(subjectId, trimmed) : action === 'detach_classes' ? await detachLearnerFromClasses(subjectId, trimmed) : await unlinkParent(subjectId, parentId, trimmed);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? 'De handeling is niet uitgevoerd.');
      return;
    }

    // NUL IS GEEN SUCCES. Er was niets om te repareren, en dat moet hier staan:
    // anders meldt support aan de school dat het is opgelost.
    if (action === 'issue_code' && res.code) {
      setIssuedCode(res.code);
      setResult(`Nieuwe koppelcode voor ${subjectName}. De vorige code werkt niet meer — ook niet voor wie hem al had.`);
    } else if (action === 'revoke_recovery') {
      setResult(res.affected ? `De herstelcode van ${subjectName} is ongeldig. ${subjectName} kan in de instellingen zelf een nieuwe aanmaken.` : `Er was geen herstelcode om in te trekken. Er is niets gewijzigd.`);
    } else if (action === 'detach_classes') {
      setResult(res.affected ? `${subjectName} is uit ${res.affected} klas(sen) gehaald. De voortgang blijft staan en gaat mee naar een nieuwe school.` : `${subjectName} zat niet in een klas. Er is niets gewijzigd.`);
    } else {
      setResult(res.affected ? `De koppeling is verbroken.` : `Deze koppeling bestond niet (meer). Er is niets gewijzigd — controleer of een collega dit al heeft gedaan.`);
    }
    close();
  };
  const actions: Array<{
    id: ActionId;
    label: string;
    icon: typeof KeyRound;
    hint: string;
    show: boolean;
  }> = [{
    id: 'issue_code',
    label: 'Nieuwe koppelcode uitgeven',
    icon: LinkIcon,
    hint: 'Voor een ouder die geen toegang krijgt. De ouder verzilvert de code zelf — koppelen namens iemand kan niet, dat zou de hele codebeveiliging omzeilen.',
    show: role === 'learner'
  }, {
    id: 'unlink_parent',
    label: 'Ouderkoppeling verbreken',
    icon: Unlink,
    hint: 'Voor een melding dat er iemand meekijkt die dat niet mag. Dit wist de relatie, niet het account van de ouder en niet de gegevens van het kind.',
    show: role === 'learner'
  }, {
    id: 'detach_classes',
    label: 'Uit alle klassen halen',
    icon: ShieldOff,
    hint: 'Voor een kind dat van school is gewisseld terwijl de oude leerkracht nog meekijkt. De nieuwe leerkracht koppelt het kind zelf met de klascode.',
    show: role === 'learner'
  }, {
    id: 'revoke_recovery',
    label: 'Herstelcode intrekken',
    icon: KeyRound,
    hint: 'Voor een herstelcode die op straat ligt. Die code geeft een wachtwoordreset, dus hij bewijst identiteit — het zwaarste artefact dat we hebben.',
    show: true
  }];
  const visible = actions.filter(a => a.show);
  return <section aria-labelledby="support-actions-heading" className="rounded-xl border border-slate-200 bg-white p-4" data-id="element-7863" data-mp="3klkwzge34n7s">
      <h3 id="support-actions-heading" className="text-sm font-bold text-slate-900" data-id="element-7864" data-mp="3fuumxpuprw6r">
        Supportacties
      </h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-600" data-id="element-7865" data-mp="3awt8xjc5an25">
        Deze reparaties hebben geen toegangsvenster nodig — er wordt niets van
        {' '}
        {subjectName} gelezen. Ze vragen wél een reden, en die wordt vastgelegd
        onder jouw naam.
      </p>

      {result && <p role="status" className="mt-3 rounded-lg bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-900" data-id="element-7866" data-mp="2h9gunn7naoo">
          {result}
        </p>}

      {issuedCode && <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2" data-id="element-7867" data-mp="3pkxssqmxp4mi">
          <span className="font-mono text-lg font-black tracking-widest text-emerald-800" data-id="element-7868" data-mp="2i7ma0j8s3vgd">
            {issuedCode}
          </span>
          <button onClick={() => void navigator.clipboard.writeText(issuedCode)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-50" data-id="element-7869" data-mp="2e47iavbh9qn3">
            <Copy className="h-3 w-3" aria-hidden="true" data-id="element-7870" data-mp="xn8tfljsx9zx" />
            Kopieer
          </button>
        </div>}

      {error && <p role="alert" className="mt-3 rounded-lg bg-rose-50 p-3 text-xs leading-relaxed text-rose-900" data-id="element-7871" data-mp="17f4qcta5iu2k">
          {error}
        </p>}

      <ul className="mt-3 space-y-2" data-id="element-7872" data-mp="1cd69tfbhovek">
        {visible.map(action => <li key={action.id} className="rounded-lg border border-slate-200" data-id="element-7873" data-mp="1hdapj34kpm3q">
            {open === action.id ? <div className="p-3" data-id="element-7874" data-mp="1lew8n67pawmd">
                <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900" data-id="element-7875" data-mp="1lfz29lwerd5z">
                  <action.icon className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" data-id="element-7876" data-mp="1qcxtnwoot60h" />
                  {action.label}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-slate-600" data-id="element-7877" data-mp="3n8dhuebhc5j8">
                  {action.hint}
                </p>

                {action.id === 'unlink_parent' && (knownParents.length > 0 ? <label className="mt-3 block" data-id="element-7878" data-mp="1xvp06ag0zs8n">
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7879" data-mp="1twg24tdyd87a">
                        Welke ouder
                      </span>
                      <select value={parentId} onChange={e => setParentId(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm" data-id="element-7880" data-mp="27p5stb4ff1aq">
                        <option value="" data-id="element-7881" data-mp="1ez5ujv4fkmgu">Kies…</option>
                        {knownParents.map(p => <option key={p.id} value={p.id} data-id="element-7882" data-mp="1ay3td8qye0c8">
                            {p.name}
                          </option>)}
                      </select>
                    </label> : <p className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900" data-id="element-7883" data-mp="1jy0b0qexdyxs">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-7884" data-mp="30b7bllh561cx" />
                      <span data-id="element-7885" data-mp="2vc44zwlijn2u">
                        Dit apparaat kent de gekoppelde ouders van {subjectName}
                        {' '}
                        niet — dat betekent niet dat er niemand gekoppeld is.
                        Vraag de school of het kind om het account dat weg moet.
                      </span>
                    </p>)}

                <label className="mt-3 block" data-id="element-7886" data-mp="d3jdsj3ep3mp">
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7887" data-mp="d4cbkuke0isp">
                    Reden
                  </span>
                  <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="Waar kwam deze melding vandaan en wat is er afgesproken?" className="mt-1 w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm" data-id="element-7888" data-mp="10vhs7by5jf42" />
                </label>
                <p className="mt-1 text-xs text-slate-500" data-id="element-7889" data-mp="w0ubw0daxr0k">
                  Minstens 10 tekens. Deze tekst is de verantwoording van de
                  ingreep, dus schrijf op wat er gemeld is — niet "op verzoek".
                </p>

                <div className="mt-3 flex justify-end gap-2" data-id="element-7890" data-mp="31419tx88tr0o">
                  <Button type="button" size="sm" variant="secondary" onClick={close} data-id="element-7891" data-mp="314uacgglzkad">
                    Annuleren
                  </Button>
                  <Button type="button" size="sm" variant="danger" disabled={busy || reasonTooShort || action.id === 'unlink_parent' && !parentId} onClick={() => void run(action.id)} data-id="element-7892" data-mp="2s3mmytfms3v2">
                    {busy ? 'Bezig…' : 'Uitvoeren'}
                  </Button>
                </div>
              </div> : <button onClick={() => {
          setOpen(action.id);
          setReason('');
          setError(null);
        }} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50" data-id="element-7893" data-mp="2n49r5j6s7mgc">
                <action.icon className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" data-id="element-7894" data-mp="2deqamk6jk4on" />
                {action.label}
              </button>}
          </li>)}
      </ul>

      {/*
       WAT BEWUST NIET KAN — als tekst, niet als uitgeschakeld veld.
        Een grijs invoervak leest als "dit komt er nog". Deze drie komen er niet,
       en de reden is per stuk anders. Ze hier weglaten zou de vraag laten
       terugkomen bij elke supportmelding.
       */}
      <div className="mt-4 rounded-lg bg-slate-50 p-3" data-id="element-7895" data-mp="1ttpxrxmscyva">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7896" data-mp="1pr32tnutixtv">
          Wat een beheerder niet kan
        </p>
        <ul className="mt-1.5 space-y-1.5 text-xs leading-relaxed text-slate-600" data-id="element-7897" data-mp="23q2qas6lh6og">
          <li data-id="element-7898" data-mp="1ys1calo0zxju">
            <strong data-id="element-7899" data-mp="1ysjdzxtgj3z5">Naam of groep wijzigen.</strong> Dat hoort bij wie
            verantwoordelijke is: de ouder of de school. Een beheerder die dat
            kan, is een tweede ouder.
          </li>
          <li data-id="element-7900" data-mp="28hb4s2ms7i8h">
            <strong data-id="element-7901" data-mp="28ivuney5id00">Een wachtwoord zetten.</strong> Wie dat kan, kan daarna
            inloggen als die persoon — en dan is elke grens hier omzeilbaar,
            inclusief dit logboek.
          </li>
          <li data-id="element-7902" data-mp="2hjk8o9njodcw">
            <strong data-id="element-7903" data-mp="2hk2f1z3x87yj">Een ouder rechtstreeks koppelen.</strong> Dat zou de
            koppelcode-architectuur omzeilen via support. Gebruik in plaats
            daarvan een nieuwe koppelcode.
          </li>
        </ul>
      </div>
    </section>;
}